import React from "react";
import { Gauge, Zap, CalendarClock, ArrowRightLeft, Clock, ShieldAlert } from "lucide-react";
import { SX } from "../studioTokens";
import { Banner } from "./BillingBits";
import { TRIAL_PLAN_KEY, fmtDate, fmtNum, daysUntil } from "./billingConst";

/* Contextual strips above the plan card, a failed renewal payment included
   (the card itself carries the "Update payment method" button for it). */
const StatusBanners = ({ balance, planName, activating, onBuyCredits, onViewPlans }) => {
  const isTrial = balance.plan_key === TRIAL_PLAN_KEY;
  const remaining = Number(balance.balance ?? balance.credits ?? 0);
  const allocation = Number(balance.allocation_credits ?? balance.monthly_credits ?? 0);
  const used = Number(balance.used_this_period ?? Math.max(allocation - remaining, 0));
  const usedPct = allocation > 0 ? (used / allocation) * 100 : 0;
  const healthy = balance.status === "trial" || balance.status === "active";

  const out = [];

  if (balance.status === "payment_required" || balance.status === "paused") {
    const paused = balance.status === "paused";
    const recoveryDays = daysUntil(balance.payment_recovery_deadline);
    out.push(
      <Banner key="payment" tone="danger" icon={ShieldAlert} title={paused ? "Subscription paused" : "Payment required"}>
        {paused
          ? "Your renewal payment wasn't resolved in time, so the subscription is paused and extraction is disabled. Update your payment method to restore access — your remaining credits are preserved."
          : "Your renewal payment couldn't be processed, so extraction is paused until it goes through. Update your payment method to continue."}
        {recoveryDays != null && !paused && (
          <span className="font-semibold">
            {" "}
            {recoveryDays === 0
              ? "The recovery window closes today."
              : `${recoveryDays} day${recoveryDays === 1 ? "" : "s"} left to resolve this before the subscription pauses.`}
          </span>
        )}
      </Banner>
    );
  }

  if (activating) {
    out.push(
      <Banner key="activating" tone="info" icon={Clock} title="Payment received — activating your plan…">
        Stripe is confirming the payment. This usually takes a few seconds; the balance below refreshes on its own.
      </Banner>
    );
  }

  if (healthy && remaining <= 0) {
    out.push(
      isTrial ? (
        <Banner
          key="credits"
          tone="danger"
          icon={Zap}
          title="You've used all your trial credits."
          actions={<button className={`${SX.btn} ${SX.btnSm} ${SX.btnPrimary}`} onClick={onViewPlans}>Choose a plan</button>}
        >
          Pick a paid plan to keep extracting — extraction is blocked while your balance is zero.
        </Banner>
      ) : (
        <Banner
          key="credits"
          tone="warn"
          icon={Gauge}
          title="You're out of credits."
          actions={
            <>
              {balance.can_buy_addons && (
                <button className={`${SX.btn} ${SX.btnSm} ${SX.btnSoft}`} onClick={onBuyCredits}>Buy a credit pack</button>
              )}
              <button className={`${SX.btn} ${SX.btnSm} ${SX.btnPrimary}`} onClick={onViewPlans}>Change plan</button>
            </>
          }
        >
          Extraction is paused until you top up or move to a larger plan.
        </Banner>
      )
    );
  } else if (healthy && usedPct >= 85) {
    out.push(
      <Banner
        key="credits"
        tone="warn"
        icon={Gauge}
        title="You're running low on credits."
        actions={
          <>
            {balance.can_buy_addons && (
              <button className={`${SX.btn} ${SX.btnSm} ${SX.btnSoft}`} onClick={onBuyCredits}>Buy credits</button>
            )}
            <button className={`${SX.btn} ${SX.btnSm} ${SX.btnGhost}`} onClick={onViewPlans}>View plans</button>
          </>
        }
      >
        {fmtNum(remaining)} credits left of your {fmtNum(allocation)}-credit allocation
        {balance.period_end ? ` — this period ends ${fmtDate(balance.period_end)}.` : "."}
      </Banner>
    );
  }

  if (balance.cancel_at_period_end) {
    out.push(
      <Banner key="cancelled" tone="neutral" icon={CalendarClock} title="Your subscription is set to cancel.">
        You keep full access until {fmtDate(balance.period_end)}. After that the account moves to expired and you can
        re-subscribe from the plans below. To undo this, reopen the Stripe billing portal.
      </Banner>
    );
  }

  if (balance.pending_plan_key && balance.pending_plan_key !== balance.plan_key) {
    out.push(
      <Banner key="pending" tone="info" icon={ArrowRightLeft} title={`Switching to ${planName || balance.pending_plan_key}`}>
        The change takes effect on {fmtDate(balance.period_end)}. Your current allocation stays in place until then.
      </Banner>
    );
  }

  if (!out.length) return null;
  return <div className="flex flex-col gap-3">{out}</div>;
};

export default StatusBanners;
